import { type FC, useCallback, type MouseEventHandler } from 'react';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import { graphql, useFragment } from 'react-relay';

import { Button } from 'dibs-elements/exports/Button';
import { trackEvent, eventNameConstants, interactionTypeConstants } from 'dibs-tracking';

import { type PdpSharedAddToCartButton_user$key } from './__generated__/PdpSharedAddToCartButton_user.graphql';
import { type PdpSharedAddToCartButton_item$key } from './__generated__/PdpSharedAddToCartButton_item.graphql';
import { useAddItemToCart } from 'dibs-buyer-layout/exports/useAddItemToCart';

const userFragment = graphql`
    fragment PdpSharedAddToCartButton_user on User {
        ...useAddItemToCart_user
    }
`;
const itemFragment = graphql`
    fragment PdpSharedAddToCartButton_item on Item {
        ...useAddItemToCart_item
    }
`;

export const PdpSharedAddToCartButton: FC<{
    user: PdpSharedAddToCartButton_user$key | null | undefined;
    item: PdpSharedAddToCartButton_item$key;
    skuId?: string;
    quantitySelected?: string;
}> = ({ user: userRef, item: itemRef, skuId, quantitySelected }) => {
    const intl = useIntl();
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);

    const onClick: MouseEventHandler = useCallback(
        event => {
            trackEvent(
                {
                    action: 'pdp add to cart button',
                    category: 'add to cart click',
                    eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                    interaction_type: interactionTypeConstants.INTERACTION_TYPE_ADD_TO_CART,
                },
                event
            );
            window.location.assign(handleLocaleUrl('/my/cart/', intl.locale));
        },
        [intl.locale]
    );
    const { addItemToCart, isAddingItemToCart } = useAddItemToCart({
        quantitySelected: quantitySelected || '1',
        onClick,
        item,
        user,
        skuId: skuId || null,
    });

    return (
        <Button
            fullWidth
            type="secondary"
            size="large"
            onClick={e => addItemToCart(e)}
            disabled={isAddingItemToCart}
        >
            <FormattedMessage
                id="PdpSharedAddToCartButton.addToCart"
                defaultMessage="Add to cart"
            />
        </Button>
    );
};
